import React, { useMemo } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import DataTable, { type ColumnConfig } from '../../Shared/Components/DataTable';
import SearchFormWrapper, { SearchFormTypes } from '../../Shared/Components/SearchFormWrapper';
import type { FieldConfig } from '../../Shared/Components/SearchFormWrapper';
import { formatDisplayDate, today } from '../../Shared/Helpers/dateHelpers';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import type { AccountingExportApiErrorResponse } from '../Service/accountingExportApi.contract';
import { resolveAccountingExportErrorCode } from '../Service/accountingExportApi.contract';
import { useGetAccountingExportCompaniesLookupQuery } from '../Service/accountingExportCompaniesLookupApi';
import {
  useExportAccountingPurchasesEdiMutation,
  useExportAccountingPurchasesNexoXmlMutation,
  useExportAccountingSalesEdiMutation,
  useExportAccountingSalesJpkFa4Mutation,
  useExportAccountingSalesNexoXmlMutation,
  useGetAccountingExportItemsQuery,
} from '../Service/accountingExportApi';
import type { AccountingExportDocumentItem } from '../Types/accountingExportTypes';
import { useAccountingExportFilters, type FeedbackState } from './useAccountingExportFilters';

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const buildSalesExportFileName = (companyId: string, dateFrom: string, dateTo: string): string =>
  `accounting-sales-jpk-fa4_${companyId}_${dateFrom || 'all'}_${dateTo || today()}.xml`;

const buildSalesEdiExportFileName = (companyId: string, dateFrom: string, dateTo: string): string =>
  `accounting-sales-edi_${companyId}_${dateFrom || 'all'}_${dateTo || today()}.epp`;

const buildPurchasesExportFileName = (companyId: string, dateFrom: string, dateTo: string): string =>
  `accounting-purchases-edi_${companyId}_${dateFrom || 'all'}_${dateTo || today()}.epp`;

const buildSalesNexoXmlExportFileName = (companyId: string, dateFrom: string, dateTo: string): string =>
  `TravelCRM_sprzedaz_nexo_${companyId}_${dateFrom || 'all'}_${dateTo || today()}.xml`;

const buildPurchasesNexoXmlExportFileName = (companyId: string, dateFrom: string, dateTo: string): string =>
  `TravelCRM_zakupy_nexo_${companyId}_${dateFrom || 'all'}_${dateTo || today()}.xml`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getAccountingExportErrorPayload = (
  error: unknown
): AccountingExportApiErrorResponse | null => {
  if (!isRecord(error) || !('data' in error)) return null;
  const data = error.data;
  if (typeof data === 'string') return data;
  return isRecord(data) ? data : null;
};

const getExportErrorFeedback = (error: unknown, fallbackMessage: string): FeedbackState => {
  const payload = getAccountingExportErrorPayload(error);
  const errorCode = resolveAccountingExportErrorCode(payload);

  if (errorCode === 'AccountingExport.DuplicateExport') {
    return {
      severity: 'warning',
      message: 'Eksport został już wcześniej wygenerowany dla wskazanego zakresu. Pobierz istniejący plik lub zmień filtry.',
    };
  }

  if (errorCode === 'AccountingExport.EligibilityBlocked') {
    return {
      severity: 'error',
      message: 'Eksport jest zablokowany dla części dokumentów. Sprawdź kolumnę "Powody blokady" i popraw dane źródłowe.',
    };
  }

  return { severity: 'error', message: extractApiErrorMessage(error, fallbackMessage) };
};

const getAccountingExportRowId = (item: AccountingExportDocumentItem): number => {
  const stableKey = `${item.source}:${item.documentId}`;
  let hashA = 0x811c9dc5;
  let hashB = 0x9e3779b1;
  for (let i = 0; i < stableKey.length; i += 1) {
    const c = stableKey.charCodeAt(i);
    hashA = Math.imul(hashA ^ c, 16777619);
    hashB = Math.imul(hashB ^ c, 2246822519);
  }
  const high = (hashA >>> 0) & 0x1fffff;
  const low = hashB >>> 0;
  const hash53 = high * 4294967296 + low;
  return hash53 === 0 ? 1 : hash53;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return 'Nie';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Tak';
  return `Tak — ${date.toLocaleString('pl-PL')}`;
};

const formatSource = (source: AccountingExportDocumentItem['source']): string => {
  switch (source) {
    case 'BookingSnapshot': return 'Booking';
    case 'SalesInvoice': return 'Sprzedaż standalone';
    case 'PurchaseInvoice': return 'Zakup standalone';
    default: return source;
  }
};

const formatChannel = (channel: AccountingExportDocumentItem['channel']): string =>
  channel === 'Sales' ? 'Sprzedaż' : 'Zakup';

const formatAmount = (value: number): string => currencyFormatter.format(value);

const formatBookingContext = (item: AccountingExportDocumentItem): string => {
  if (item.source !== 'BookingSnapshot') return '—';
  if (item.bookingContextLabel) return item.bookingContextLabel;
  if (item.offerName && item.bookingDateFrom && item.bookingDateTo) {
    return `${item.offerName} | ${formatDisplayDate(item.bookingDateFrom)}-${formatDisplayDate(item.bookingDateTo)}`;
  }
  return '—';
};

const AccountingExportsPage = () => {
  const {
    filters,
    validationError,
    feedback,
    setFeedback,
    hasValidAppliedFilters,
    listQuery,
    exportPreconditionFeedback,
    handleSearch,
    handleReset,
  } = useAccountingExportFilters();

  const { data, isFetching, isLoading, isError, error, refetch } = useGetAccountingExportItemsQuery(
    listQuery,
    { skip: !hasValidAppliedFilters, refetchOnMountOrArgChange: true }
  );

  const { companies, isLoading: isCompaniesLoading } = useGetAccountingExportCompaniesLookupQuery();

  const [exportAccountingSales, { isLoading: isSalesExporting }] =
    useExportAccountingSalesJpkFa4Mutation();
  const [exportAccountingSalesEdi, { isLoading: isSalesEdiExporting }] =
    useExportAccountingSalesEdiMutation();
  const [exportAccountingPurchases, { isLoading: isPurchasesExporting }] =
    useExportAccountingPurchasesEdiMutation();
  const [exportAccountingSalesNexoXml, { isLoading: isSalesNexoXmlExporting }] =
    useExportAccountingSalesNexoXmlMutation();
  const [exportAccountingPurchasesNexoXml, { isLoading: isPurchasesNexoXmlExporting }] =
    useExportAccountingPurchasesNexoXmlMutation();
  const isAnyExporting =
    isSalesExporting ||
    isSalesEdiExporting ||
    isPurchasesExporting ||
    isSalesNexoXmlExporting ||
    isPurchasesNexoXmlExporting;

  const fields: FieldConfig<Record<string, string>>[] = useMemo(
    () => [
      { name: 'dateFrom', label: 'Data od', type: SearchFormTypes.date },
      { name: 'dateTo', label: 'Data do', type: SearchFormTypes.date },
      { name: 'companyId', label: 'Spółka', type: SearchFormTypes.autocomplete, options: companies },
      {
        name: 'source',
        label: 'Źródło',
        type: SearchFormTypes.select,
        options: [
          { id: 'booking', name: 'Booking' },
          { id: 'standalone', name: 'Standalone' },
        ],
      },
      {
        name: 'channel',
        label: 'Kanał',
        type: SearchFormTypes.select,
        options: [
          { id: 'sales', name: 'Sprzedaż' },
          { id: 'purchases', name: 'Zakup' },
        ],
      },
      {
        name: 'sentToAccounting',
        label: 'Wysłane do księgowości',
        type: SearchFormTypes.select,
        options: [
          { id: 'true', name: 'Tak' },
          { id: 'false', name: 'Nie' },
        ],
      },
    ],
    [companies]
  );

  const columns: ColumnConfig<AccountingExportDocumentItem>[] = useMemo(
    () => [
      { key: 'number', label: 'Numer' },
      { key: 'bookingContextLabel', label: 'Kontekst terminu', render: (row) => formatBookingContext(row) },
      { key: 'source', label: 'Źródło', render: (row) => formatSource(row.source) },
      { key: 'channel', label: 'Kanał', render: (row) => formatChannel(row.channel) },
      { key: 'documentType', label: 'Typ dokumentu' },
      { key: 'scheme', label: 'Schemat', render: (row) => row.scheme || '—' },
      { key: 'issueDate', label: 'Data wystawienia', render: (row) => formatDisplayDate(row.issueDate) },
      { key: 'saleDate', label: 'Data sprzedaży', render: (row) => formatDisplayDate(row.saleDate) },
      { key: 'taxDate', label: 'Data podatkowa', render: (row) => formatDisplayDate(row.taxDate) },
      { key: 'net', label: 'Netto', render: (row) => formatAmount(row.net) },
      { key: 'vat', label: 'VAT', render: (row) => formatAmount(row.vat) },
      { key: 'gross', label: 'Brutto', render: (row) => formatAmount(row.gross) },
      { key: 'sentToAccountingAtUtc', label: 'Wysłane', render: (row) => formatDateTime(row.sentToAccountingAtUtc) },
      {
        key: 'exportEligibilityStatus',
        label: 'Status eksportu',
        render: (row) => (row.exportEligibilityStatus === 'Eligible' ? 'Gotowy' : 'Zablokowany'),
      },
      {
        key: 'blockingReasons',
        label: 'Powody blokady',
        render: (row) => {
          if (row.blockingReasons.length === 0) return '—';
          return (
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {row.blockingReasons.map((reason) => (
                <li key={`${row.documentId}-${reason.code}`}>
                  {reason.message} ({reason.code})
                </li>
              ))}
            </Box>
          );
        },
      },
    ],
    []
  );

  const handleSalesExport = async () => {
    if (exportPreconditionFeedback) {
      setFeedback(exportPreconditionFeedback);
      return;
    }

    try {
      setFeedback(null);
      const result = await exportAccountingSales({
        companyId: Number(filters.companyId),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        filename: buildSalesExportFileName(filters.companyId, filters.dateFrom, filters.dateTo),
      }).unwrap();

      setFeedback({ severity: 'success', message: 'Eksport sprzedaży JPK_FA(4) został wygenerowany.' });
    } catch (salesExportError) {
      setFeedback(getExportErrorFeedback(salesExportError, 'Nie udało się wygenerować eksportu sprzedaży JPK_FA(4) dla wskazanego zakresu.'));
    }
  };

  const handleSalesEdiExport = async () => {
    if (exportPreconditionFeedback) {
      setFeedback(exportPreconditionFeedback);
      return;
    }

    try {
      setFeedback(null);
      await exportAccountingSalesEdi({
        companyId: Number(filters.companyId),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        filename: buildSalesEdiExportFileName(filters.companyId, filters.dateFrom, filters.dateTo),
      }).unwrap();

      setFeedback({ severity: 'success', message: 'Eksport sprzedaży EDI++ został wygenerowany.' });
    } catch (salesEdiExportError) {
      setFeedback(getExportErrorFeedback(salesEdiExportError, 'Nie udało się wygenerować eksportu sprzedaży EDI++ dla wskazanego zakresu.'));
    }
  };

  const handlePurchasesExport = async () => {
    if (exportPreconditionFeedback) {
      setFeedback(exportPreconditionFeedback);
      return;
    }

    try {
      setFeedback(null);
      await exportAccountingPurchases({
        companyId: Number(filters.companyId),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        filename: buildPurchasesExportFileName(filters.companyId, filters.dateFrom, filters.dateTo),
      }).unwrap();

      setFeedback({ severity: 'success', message: 'Eksport zakupów EDI++ został wygenerowany.' });
    } catch (purchasesExportError) {
      setFeedback(getExportErrorFeedback(purchasesExportError, 'Nie udało się wygenerować eksportu zakupów EDI++ dla wskazanego zakresu.'));
    }
  };

  const handleSalesNexoXmlExport = async () => {
    if (exportPreconditionFeedback) {
      setFeedback(exportPreconditionFeedback);
      return;
    }

    try {
      setFeedback(null);
      await exportAccountingSalesNexoXml({
        companyId: Number(filters.companyId),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        filename: buildSalesNexoXmlExportFileName(filters.companyId, filters.dateFrom, filters.dateTo),
      }).unwrap();

      setFeedback({ severity: 'success', message: 'Eksport sprzedaży nexo XML został wygenerowany.' });
    } catch (salesNexoXmlExportError) {
      const nexoSalesPayload = getAccountingExportErrorPayload(salesNexoXmlExportError);
      const nexoSalesErrorCode = resolveAccountingExportErrorCode(nexoSalesPayload);
      if (nexoSalesErrorCode === 'AccountingExport.EligibilityBlocked') {
        setFeedback({
          severity: 'error',
          message: 'Eksport nexo XML sprzedaży jest zablokowany — faktury marży w tym samym bookingu mają różnych nabywców. Sprawdź dane kontrahentów w dokumentach FM.',
        });
      } else {
        setFeedback(getExportErrorFeedback(salesNexoXmlExportError, 'Nie udało się wygenerować eksportu sprzedaży nexo XML dla wskazanego zakresu.'));
      }
    }
  };

  const handlePurchasesNexoXmlExport = async () => {
    if (exportPreconditionFeedback) {
      setFeedback(exportPreconditionFeedback);
      return;
    }

    try {
      setFeedback(null);
      await exportAccountingPurchasesNexoXml({
        companyId: Number(filters.companyId),
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        filename: buildPurchasesNexoXmlExportFileName(filters.companyId, filters.dateFrom, filters.dateTo),
      }).unwrap();

      setFeedback({ severity: 'success', message: 'Eksport zakupów nexo XML został wygenerowany.' });
    } catch (purchasesNexoXmlExportError) {
      setFeedback(getExportErrorFeedback(purchasesNexoXmlExportError, 'Nie udało się wygenerować eksportu zakupów nexo XML dla wskazanego zakresu.'));
    }
  };

  return (
    <>
      <CustomCard
        icon={<AccountBalanceWalletIcon fontSize="large" color="primary" />}
        title="Eksporty księgowe"
        subheader="Dokumenty booking + standalone dla JPK_FA(4) i EDI++"
        sx={{ mb: 4 }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Wybierz spółkę i ustaw filtry. Dla zakresu obejmującego sprzedaż standalone wymagany jest
            pełny zakres dat od/do.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabela zawiera dokumenty booking i standalone. Przy statusie zablokowanym sprawdź kolumnę
            z powodami blokady.
          </Typography>
        </Box>

        <SearchFormWrapper
          fields={fields}
          initialValues={filters}
          onSubmit={(values) => handleSearch(values, () => { void refetch(); })}
          onReset={handleReset}
          resetValues={{ dateFrom: '', dateTo: '', companyId: '', source: '', channel: '', sentToAccounting: '' }}
          showResetButton
          validationError={validationError}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant="contained"
            startIcon={isSalesExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleSalesExport}
            disabled={isAnyExporting || isFetching || !hasValidAppliedFilters}
          >
            {isSalesExporting ? 'Trwa generowanie JPK_FA(4)...' : 'Eksportuj sprzedaż JPK_FA(4)'}
          </Button>

          <Button
            variant="outlined"
            startIcon={isSalesEdiExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleSalesEdiExport}
            disabled={isAnyExporting || isFetching || !hasValidAppliedFilters}
          >
            {isSalesEdiExporting ? 'Trwa generowanie EDI++...' : 'Eksportuj sprzedaż EDI++'}
          </Button>

          <Button
            variant="outlined"
            startIcon={isPurchasesExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handlePurchasesExport}
            disabled={isAnyExporting || isFetching || !hasValidAppliedFilters}
          >
            {isPurchasesExporting ? 'Trwa generowanie EDI++...' : 'Eksportuj zakupy EDI++'}
          </Button>

          <Button
            variant="outlined"
            startIcon={isSalesNexoXmlExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handleSalesNexoXmlExport}
            disabled={isAnyExporting || isFetching || !hasValidAppliedFilters}
          >
            {isSalesNexoXmlExporting ? 'Trwa generowanie nexo XML...' : 'Pobierz nexo XML sprzedaż'}
          </Button>

          <Button
            variant="outlined"
            startIcon={isPurchasesNexoXmlExporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={handlePurchasesNexoXmlExport}
            disabled={isAnyExporting || isFetching || !hasValidAppliedFilters}
          >
            {isPurchasesNexoXmlExporting ? 'Trwa generowanie nexo XML...' : 'Pobierz nexo XML zakupy'}
          </Button>

          {isCompaniesLoading && (
            <Typography variant="body2" color="text.secondary">
              Trwa ładowanie listy spółek...
            </Typography>
          )}
        </Stack>

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mt: 2 }}>
            {feedback.message}
          </Alert>
        )}
      </CustomCard>

      <CustomCard>
        {!hasValidAppliedFilters ? (
          <Alert severity="info">
            Wybierz spółkę i zastosuj filtry, aby pobrać dokumenty do eksportu księgowego.
          </Alert>
        ) : (
          <DataTable
            columns={columns}
            data={data ?? []}
            isLoading={isLoading || isFetching}
            isError={isError}
            errorMessage={extractApiErrorMessage(error, 'Nie udało się pobrać listy dokumentów do eksportu.')}
            getRowId={getAccountingExportRowId}
            emptyMessage="Brak dokumentów dla wybranych filtrów."
          />
        )}
      </CustomCard>
    </>
  );
};

export default AccountingExportsPage;
